import type {
  AssetDiagnosticDownloadResponseDto,
  AssetDiagnosticResponseDto,
  ListAssetDiagnosticsResponseDto,
} from '@evimo/types';
import { ResponseError } from '@evimo/client/runtime';
import {
  assetDiagnosticsRequestInit,
  assetOcppDiagnosticsApi,
} from '~/app/services/asset/asset-diagnostics-api-instances';

async function readClientErrorMessage(
  error: unknown,
  fallback: string,
): Promise<string> {
  if (!(error instanceof ResponseError)) {
    return error instanceof Error ? error.message : fallback;
  }
  const payload = (await error.response.json().catch(() => null)) as {
    message?: string | string[];
    reason?: string;
  } | null;
  if (Array.isArray(payload?.message)) {
    return payload.message.join('; ');
  }
  if (typeof payload?.message === 'string' && payload.message.trim()) {
    return payload.message;
  }
  if (typeof payload?.reason === 'string' && payload.reason.trim()) {
    return payload.reason;
  }
  return fallback;
}

export class AssetDiagnosticsUnavailableError extends Error {
  readonly status: number;
  readonly reason?: string;

  constructor(status: number, message: string, reason?: string) {
    super(message);
    this.name = 'AssetDiagnosticsUnavailableError';
    this.status = status;
    this.reason = reason;
  }
}

export type ListAssetDiagnosticsParams = {
  page: number;
  limit: number;
};

export const AssetDiagnosticsService = {
  async listByAssetId(
    organizationId: string,
    assetId: string,
    params: ListAssetDiagnosticsParams,
  ): Promise<ListAssetDiagnosticsResponseDto> {
    const normalizedOrganizationId = organizationId.trim();
    const normalizedAssetId = assetId.trim();

    if (!normalizedOrganizationId) {
      throw new Error('Organization id is required');
    }

    if (!normalizedAssetId) {
      throw new Error('Asset id is required');
    }

    try {
      const result =
        await assetOcppDiagnosticsApi.ocppDiagnosticsQueryControllerList(
          {
            organizationId: normalizedOrganizationId,
            assetId: normalizedAssetId,
            page: params.page,
            limit: params.limit,
          },
          assetDiagnosticsRequestInit,
        );
      return result as unknown as ListAssetDiagnosticsResponseDto;
    } catch (error) {
      throw new Error(
        await readClientErrorMessage(
          error,
          'Failed to load diagnostics history',
        ),
      );
    }
  },

  async createDownloadUrl(
    organizationId: string,
    assetId: string,
    diagnosticId: string,
  ): Promise<AssetDiagnosticDownloadResponseDto> {
    const normalizedOrganizationId = organizationId.trim();
    const normalizedAssetId = assetId.trim();
    const normalizedDiagnosticId = diagnosticId.trim();

    if (!normalizedOrganizationId) {
      throw new Error('Organization id is required');
    }

    if (!normalizedAssetId || !normalizedDiagnosticId) {
      throw new Error('Asset id and diagnostic id are required');
    }

    try {
      const result =
        await assetOcppDiagnosticsApi.ocppDiagnosticsQueryControllerDownload(
          {
            organizationId: normalizedOrganizationId,
            assetId: normalizedAssetId,
            diagnosticId: normalizedDiagnosticId,
          },
          assetDiagnosticsRequestInit,
        );
      return result as unknown as AssetDiagnosticDownloadResponseDto;
    } catch (error) {
      if (error instanceof ResponseError) {
        const payload = (await error.response.json().catch(() => null)) as {
          message?: string | string[];
          reason?: string;
        } | null;
        const message = Array.isArray(payload?.message)
          ? payload.message.join('; ')
          : typeof payload?.message === 'string' && payload.message.trim()
            ? payload.message
            : 'Failed to create diagnostics download URL';
        throw new AssetDiagnosticsUnavailableError(
          error.response.status,
          message,
          payload?.reason,
        );
      }
      throw error instanceof Error
        ? error
        : new Error('Failed to create diagnostics download URL');
    }
  },

  async cancel(
    organizationId: string,
    assetId: string,
    diagnosticId: string,
  ): Promise<AssetDiagnosticResponseDto> {
    const normalizedOrganizationId = organizationId.trim();
    const normalizedAssetId = assetId.trim();
    const normalizedDiagnosticId = diagnosticId.trim();

    if (!normalizedOrganizationId) {
      throw new Error('Organization id is required');
    }

    if (!normalizedAssetId || !normalizedDiagnosticId) {
      throw new Error('Asset id and diagnostic id are required');
    }

    try {
      const result =
        await assetOcppDiagnosticsApi.ocppDiagnosticsQueryControllerCancel(
          {
            organizationId: normalizedOrganizationId,
            assetId: normalizedAssetId,
            diagnosticId: normalizedDiagnosticId,
          },
          assetDiagnosticsRequestInit,
        );
      return result as unknown as AssetDiagnosticResponseDto;
    } catch (error) {
      throw new Error(
        await readClientErrorMessage(
          error,
          'Failed to cancel diagnostics request',
        ),
      );
    }
  },
};
